import type { Actions, Subjects } from '@cast/shared-ability-data-access';
import { lazyRetry } from '@cast/utils';

import {
  BASE_PATHS,
  buildClusterInventoryNodesPath,
  CLUSTER_COST_REPORT_DEFAULT_SUB_PATHS,
  CLUSTER_COST_REPORT_SEGMENTS,
  CLUSTER_INVENTORY_SEGMENTS,
} from '@oneclick/constants/navigation';

import type {
  OneClickBreadcrumbOnlyItem,
  OneClickLinkItem,
} from '@oneclick/routing/types';
import { withReportingLimit } from '@oneclick/routing/reportingLimit';

import { ClusterDashboardPage } from './ClusterDashboardPage';
import { InaccessibleClusterDashboardPage } from './InaccessibleClusterDashboardPage';
import { buildStorageAutoscalerLinks } from 'features/cluster/storage-optimization/hooks/useStorageAutoscalerLinks';

const BinPackingPage = lazyRetry(() => import('./BinPackingPage'));

const ClusterCostReportController = lazyRetry(
  () => import('@cast/split-me/pages/cluster/cost-report/CostReportController')
);
const ClusterScoreController = lazyRetry(
  () => import('@cast/split-me/pages/cluster/score/ClusterScoreController')
);
const OptimizationConstraintsPage = lazyRetry(
  () =>
    import(
      '@cast/split-me/pages/cluster/optimization-constraints/OptimizationConstraintsPage'
    )
);
const PotentialSavingsPage = lazyRetry(
  () => import('@cast/split-me/pages/cluster/savings/PotentialSavingsPage')
);
const AutoscalerController = lazyRetry(
  () => import('./OneClickAutoscalerController')
);
const InventoryController = lazyRetry(
  () => import('./inventory/InventoryController')
);
const RebalanceController = lazyRetry(
  () => import('@cast/split-me/pages/cluster/rebalance/RebalanceController')
);
const ContainerLiveMigrationController = lazyRetry(
  () =>
    import(
      '@cast/split-me/pages/cluster/container-live-migration/ContainerLiveMigrationController'
    )
);
const SpotInterruptionsPage = lazyRetry(
  () => import('@cast/split-me/pages/cluster/SpotInterruptionsPage')
);
const WorkloadOptimizationController = lazyRetry(
  () =>
    import(
      '@cast/split-me/pages/cluster/workload-optimization/WorkloadOptimizationController'
    )
);
const KarpenterController = lazyRetry(
  () => import('pages-op/cluster/karpenter/KarpenterController')
);
const KarpenterSettingsRedirectPage = lazyRetry(
  () => import('./KarpenterSettingsRedirectPage')
);
const LogsPage = lazyRetry(() => import('pages-op/cluster/LogsPageOp'));
const ClusterSettingsController = lazyRetry(
  () => import('./settings/ClusterSettingsController')
);
const StorageAutoscalerController = lazyRetry(
  () =>
    import(
      '@cast/split-me/pages/cluster/storage-autoscaler/StorageAutoscalerController'
    )
);
const ClusterVulnerabilitiesController = lazyRetry(
  () =>
    import(
      '@cast/split-me/pages/cluster/vulnerabilities/ClusterVulnerabilitiesController'
    )
);

const CLUSTER_BASE = `${BASE_PATHS.externalClusters}/:clusterId`;

type ClusterRouteWrapper =
  | 'premiumAutoscaler'
  | 'premiumWorkload'
  | 'vulnerabilities'
  | 'auditLogs';

export type ClusterLinkItem = OneClickLinkItem & {
  wrapper?: ClusterRouteWrapper;
  segment?: string;
  isPrefix?: boolean;
  hiddenWhenReportingLimited?: boolean;
};

type NavRules = Array<[Actions, Subjects]>;

export type ClusterNavMeta = {
  id: string;
  title: string;
  segment?: string;
  matchRoute: string;
  breadcrumbs: OneClickBreadcrumbOnlyItem['breadcrumbs'];
  rules?: NavRules;
  isPrefix?: boolean;
  inventoryIcon?: string;
  inventorySegment?: string;
  defaultSubPath?: string;
  hiddenWhenReportingLimited?: boolean;
};

export const CLUSTER_ROUTE_CONFIG: ClusterLinkItem[] = [
  {
    id: 'dashboard',
    title: 'Dashboard',
    route: 'index',
    page: ClusterDashboardPage,
    breadcrumbs: ['Dashboard'],
    matchRoute: CLUSTER_BASE,
    end: true,
  },
  {
    id: 'inaccessibleDashboard',
    title: 'Dashboard',
    route: 'index-inaccessible',
    page: InaccessibleClusterDashboardPage,
    breadcrumbs: ['Dashboard'],
    matchRoute: CLUSTER_BASE,
    end: true,
    routeOnly: true,
  },
  {
    id: 'costReport',
    title: 'Cost monitoring',
    route: 'cost-report/*',
    page: ClusterCostReportController,
    breadcrumbs: ['Cost monitoring'],
    matchRoute: `${CLUSTER_BASE}/cost-report`,
  },
  {
    id: 'clusterScore',
    title: 'Cluster score',
    route: 'cluster-score/*',
    page: ClusterScoreController,
    breadcrumbs: ['Cluster score'],
    matchRoute: `${CLUSTER_BASE}/cluster-score`,
    segment: 'cluster-score',
    isPrefix: true,
    rules: [['view', 'ClusterScore']],
    hiddenWhenReportingLimited: true,
  },
  {
    id: 'optimizationConstraints',
    title: 'Optimization constraints',
    route: 'optimization-constraints',
    page: OptimizationConstraintsPage,
    breadcrumbs: ['Optimization constraints'],
    matchRoute: `${CLUSTER_BASE}/optimization-constraints`,
    segment: 'optimization-constraints',
    rules: [['view', 'ClusterScoreRestrictions']],
    hiddenWhenReportingLimited: true,
  },
  {
    id: 'availableSavings',
    title: 'Available savings',
    route: 'available-savings',
    page: PotentialSavingsPage,
    breadcrumbs: ['Available savings'],
    matchRoute: `${CLUSTER_BASE}/available-savings`,
    segment: 'available-savings',
    rules: [['view', 'FeatureClusterCostMonitoringPotentialSavings']],
  },
  {
    id: 'autoscaler',
    title: 'Node autoscaler',
    route: 'autoscaler/*',
    page: AutoscalerController,
    breadcrumbs: ['Node autoscaler'],
    matchRoute: `${CLUSTER_BASE}/autoscaler`,
    wrapper: 'premiumAutoscaler',
  },
  {
    id: 'inventory',
    title: 'Inventory',
    route: 'inventory/*',
    page: InventoryController,
    breadcrumbs: ['Inventory'],
    matchRoute: `${CLUSTER_BASE}/inventory`,
  },
  {
    id: 'rebalance',
    title: 'Rebalancer',
    route: 'rebalance/*',
    page: RebalanceController,
    breadcrumbs: ['Rebalancer'],
    matchRoute: `${CLUSTER_BASE}/rebalance`,
    wrapper: 'premiumAutoscaler',
    rules: [['view', 'ClusterRebalancer']],
  },
  {
    id: 'containerLiveMigration',
    title: 'Container live migration',
    route: 'container-live-migration/*',
    page: ContainerLiveMigrationController,
    breadcrumbs: ['Container live migration'],
    matchRoute: `${CLUSTER_BASE}/container-live-migration`,
    wrapper: 'premiumAutoscaler',
    rules: [['view', 'FeatureClusterContainerLiveMigration']],
  },
  {
    id: 'spotInterruptions',
    title: 'Spot interruptions',
    route: 'spot-interruptions',
    page: SpotInterruptionsPage,
    breadcrumbs: ['Spot interruptions'],
    matchRoute: `${CLUSTER_BASE}/spot-interruptions`,
    wrapper: 'premiumAutoscaler',
    rules: [['view', 'FeatureClusterSpotInterruptions']],
  },
  {
    id: 'workloadOptimization',
    title: 'Workload autoscaler',
    route: 'workload-optimization/*',
    page: WorkloadOptimizationController,
    breadcrumbs: ['Workload autoscaler'],
    matchRoute: `${CLUSTER_BASE}/workload-optimization`,
    wrapper: 'premiumWorkload',
  },
  {
    id: 'karpenterSettingsRedirect',
    title: 'Node autoscaler',
    route: 'karpenter/settings/*',
    page: KarpenterSettingsRedirectPage,
    breadcrumbs: ['Node autoscaler'],
  },
  {
    id: 'karpenter',
    title: 'Karpenter',
    route: 'karpenter/*',
    page: KarpenterController,
    breadcrumbs: ['Karpenter'],
    matchRoute: `${CLUSTER_BASE}/karpenter`,
  },
  {
    id: 'binPacking',
    title: 'Bin-packing',
    route: 'bin-packing',
    page: BinPackingPage,
    breadcrumbs: ['Bin-packing'],
    matchRoute: `${CLUSTER_BASE}/bin-packing`,
    hiddenWhenReportingLimited: true,
  },
  {
    id: 'settings',
    title: 'Overview',
    route: 'settings/*',
    page: ClusterSettingsController,
    breadcrumbs: ['Overview'],
    matchRoute: `${CLUSTER_BASE}/settings`,
    hiddenWhenReportingLimited: true,
  },
  {
    id: 'storageAutoscaler',
    title: 'Storage autoscaler',
    route: 'storage-autoscaler/*',
    page: StorageAutoscalerController,
    breadcrumbs: ['Storage autoscaler'],
    matchRoute: `${CLUSTER_BASE}/storage-autoscaler`,
  },
  {
    id: 'vulnerabilities',
    title: 'Vulnerabilities',
    route: 'vulnerabilities/*',
    page: ClusterVulnerabilitiesController,
    breadcrumbs: ['Vulnerabilities'],
    matchRoute: `${CLUSTER_BASE}/vulnerabilities`,
    wrapper: 'vulnerabilities',
    rules: [['view', 'OrganizationSecurityVulnerabilities']],
  },
  {
    id: 'logs',
    title: 'Logs',
    route: 'logs',
    page: LogsPage,
    breadcrumbs: ['Logs'],
    matchRoute: `${CLUSTER_BASE}/logs`,
    end: true,
    wrapper: 'auditLogs',
    rules: [['view', 'ClusterAuditLog']],
  },
];

export const CLUSTER_COST_REPORT_NAV: ClusterNavMeta[] = [
  {
    id: 'clusterReport',
    title: 'Clusters',
    segment: CLUSTER_COST_REPORT_SEGMENTS.cluster,
    matchRoute: `${CLUSTER_BASE}/cost-report/${CLUSTER_COST_REPORT_SEGMENTS.cluster}`,
    breadcrumbs: ['Clusters'],
    defaultSubPath: CLUSTER_COST_REPORT_DEFAULT_SUB_PATHS.computeCost,
  },
  {
    id: 'workloadsReport',
    title: 'Workloads',
    segment: CLUSTER_COST_REPORT_SEGMENTS.workloads,
    matchRoute: `${CLUSTER_BASE}/cost-report/${CLUSTER_COST_REPORT_SEGMENTS.workloads}`,
    breadcrumbs: ['Workloads'],
  },
  {
    id: 'comparisonReport',
    title: 'Comparison',
    segment: CLUSTER_COST_REPORT_SEGMENTS.comparison,
    matchRoute: `${CLUSTER_BASE}/cost-report/${CLUSTER_COST_REPORT_SEGMENTS.comparison}`,
    breadcrumbs: ['Comparison'],
  },
  {
    id: 'savingsReport',
    title: 'Savings report',
    segment: CLUSTER_COST_REPORT_SEGMENTS.savingsReport,
    matchRoute: `${CLUSTER_BASE}/cost-report/${CLUSTER_COST_REPORT_SEGMENTS.savingsReport}`,
    breadcrumbs: ['Savings report'],
    rules: [['view', 'ValueReport']],
    hiddenWhenReportingLimited: true,
  },
];

/** Sidebar-visible cost monitoring children (excludes Workloads; includes Available savings). */
export const CLUSTER_COST_MONITORING_SIDEBAR_IDS = [
  'clusterReport',
  'comparisonReport',
  'savingsReport',
  'availableSavings',
] as const;

export const CLUSTER_INVENTORY_NAV: ClusterNavMeta[] = [
  {
    id: 'nodes',
    title: 'Nodes',
    matchRoute: `${CLUSTER_BASE}/inventory/${CLUSTER_INVENTORY_SEGMENTS.nodes}`,
    breadcrumbs: ({ params }) => {
      const clusterId = params.clusterId;
      if (!clusterId) {
        return ['Nodes'];
      }
      return [
        {
          label: 'Nodes',
          to: buildClusterInventoryNodesPath(clusterId),
        },
      ];
    },
    inventoryIcon: 'inventoryNodes',
    inventorySegment: CLUSTER_INVENTORY_SEGMENTS.nodes,
    rules: [['view', 'ClusterNodes']],
  },
  {
    id: 'workloads',
    title: 'Workloads',
    matchRoute: `${CLUSTER_BASE}/inventory/${CLUSTER_INVENTORY_SEGMENTS.workloads}`,
    breadcrumbs: ['Workloads'],
    inventoryIcon: 'workloadsReport',
    inventorySegment: CLUSTER_INVENTORY_SEGMENTS.workloads,
  },
  {
    id: 'namespaces',
    title: 'Namespaces',
    matchRoute: `${CLUSTER_BASE}/inventory/${CLUSTER_INVENTORY_SEGMENTS.namespaces}`,
    breadcrumbs: ['Namespaces'],
    inventoryIcon: 'namespacesReport',
    inventorySegment: CLUSTER_INVENTORY_SEGMENTS.namespaces,
    defaultSubPath: CLUSTER_COST_REPORT_DEFAULT_SUB_PATHS.computeCost,
  },
  {
    id: 'allocationGroups',
    title: 'Allocation groups',
    matchRoute: `${CLUSTER_BASE}/inventory/${CLUSTER_INVENTORY_SEGMENTS.allocationGroups}`,
    breadcrumbs: ['Allocation groups'],
    inventoryIcon: 'allocationGroupsReport',
    inventorySegment: CLUSTER_INVENTORY_SEGMENTS.allocationGroups,
    defaultSubPath: CLUSTER_COST_REPORT_DEFAULT_SUB_PATHS.computeCost,
  },
];

export const CLUSTER_WOOP_NAV: ClusterNavMeta[] = withReportingLimit([
  {
    id: 'workloads',
    title: 'Workloads',
    segment: 'workloads',
    matchRoute: `${CLUSTER_BASE}/workload-optimization/workloads`,
    breadcrumbs: ['Workloads'],
    rules: [['view', 'ClusterWorkloadAutoscaler']],
  },
  {
    id: 'events',
    title: 'Events',
    segment: 'events',
    matchRoute: `${CLUSTER_BASE}/workload-optimization/events`,
    breadcrumbs: ['Events'],
    rules: [['view', 'ClusterWorkloadAutoscaler']],
  },
  {
    id: 'impact',
    title: 'Impact',
    segment: 'impact',
    matchRoute: `${CLUSTER_BASE}/workload-optimization/impact`,
    breadcrumbs: ['Impact'],
    rules: [['view', 'ClusterWorkloadImpact']],
  },
  {
    id: 'customMetrics',
    title: 'Custom metrics',
    segment: 'custom-metrics',
    matchRoute: `${CLUSTER_BASE}/workload-optimization/custom-metrics`,
    breadcrumbs: ['Custom metrics'],
    rules: [['view', 'ClusterWorkloadCustomMetrics']],
  },
  {
    id: 'hpa',
    title: 'HPA',
    segment: 'hpa',
    matchRoute: `${CLUSTER_BASE}/workload-optimization/hpa`,
    breadcrumbs: ['HPA'],
    rules: [['view', 'ClusterWorkloadHpaList']],
  },
  {
    id: 'scalingPolicies',
    title: 'Scaling policies',
    segment: 'scaling-policies',
    matchRoute: `${CLUSTER_BASE}/workload-optimization/scaling-policies`,
    breadcrumbs: ['Scaling policies'],
    rules: [['view', 'ClusterWorkloadAutoscaler']],
  },
]);

export const CLUSTER_STORAGE_AUTOSCALER_NAV: ClusterNavMeta[] =
  withReportingLimit([
    {
      id: 'persistentVolumes',
      title: 'Persistent volumes',
      segment: 'persistent-volumes',
      matchRoute: `${CLUSTER_BASE}/storage-autoscaler/persistent-volumes/:pvName?`,
      breadcrumbs: ({ params }) =>
        params.pvName
          ? [
              {
                label: 'Persistent volumes',
                to: buildStorageAutoscalerLinks({
                  clusterId: params.clusterId || '',
                }).persistentVolumes(),
              },
              params.pvName,
            ]
          : ['Persistent volumes'],
      rules: [['view', 'ClusterStopPersistentVolumes']],
    },
    {
      id: 'pvPolicies',
      title: 'Policies',
      segment: 'policies',
      matchRoute: `${CLUSTER_BASE}/storage-autoscaler/policies/:policyName?`,
      breadcrumbs: ({ params }) =>
        params.policyName
          ? [
              {
                label: 'Policies',
                to: buildStorageAutoscalerLinks({
                  clusterId: params.clusterId || '',
                }).policies(),
              },
              params.policyName,
            ]
          : ['Policies'],
      rules: [['view', 'ClusterStopPvPolicies']],
    },
    {
      id: 'events',
      title: 'Events',
      segment: 'events',
      matchRoute: `${CLUSTER_BASE}/storage-autoscaler/events`,
      breadcrumbs: ['Events'],
      rules: [['view', 'ClusterStopEvents']],
    },
    {
      id: 'storageConfiguration',
      title: 'Configuration',
      segment: 'configuration',
      matchRoute: `${CLUSTER_BASE}/storage-autoscaler/configuration`,
      breadcrumbs: ['Configuration'],
      rules: [['view', 'ClusterStopConfig']],
    },
  ]);

export const CLUSTER_BREADCRUMB_ONLY: OneClickBreadcrumbOnlyItem[] = [
  ...CLUSTER_INVENTORY_NAV.map(({ matchRoute, breadcrumbs }) => ({
    matchRoute,
    breadcrumbs,
  })),
  ...CLUSTER_COST_REPORT_NAV.map(({ matchRoute, breadcrumbs }) => ({
    matchRoute,
    breadcrumbs,
  })),
  ...CLUSTER_WOOP_NAV.map(({ matchRoute, breadcrumbs }) => ({
    matchRoute,
    breadcrumbs,
  })),
  ...CLUSTER_STORAGE_AUTOSCALER_NAV.map(({ matchRoute, breadcrumbs }) => ({
    matchRoute,
    breadcrumbs,
  })),
];

export const flattenClusterNavItems = (
  items: ClusterLinkItem[]
): ClusterLinkItem[] => items;

export const getClusterMountedPages = (): ClusterLinkItem[] =>
  CLUSTER_ROUTE_CONFIG.filter(
    (item) =>
      !item.routeOnly &&
      item.route !== 'index' &&
      item.route !== 'index-inaccessible'
  );
